import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FieldValuePatchDto, FieldValuePostDto } from '@vdms-hq/api-contract';
import { SuperAdminAccessService } from '@vdms-hq/auth';
import {
  ApiFieldsSourceService,
  AssetMasterType,
  FieldTypeViewModel,
  FieldValueExtendedViewModel,
  FieldValueSiblingViewModel,
  FieldValueViewModel,
} from '@vdms-hq/selectors';
import { E2eIdDirective, OptionType, SelectOption } from '@vdms-hq/shared';
import { ToastService } from '@vdms-hq/toast';
import {
  ButtonComponent,
  DialogWrapperComponent,
  FormInputSelectComponent,
  FormInputTextAreaComponent,
  FormInputTextComponent,
  FormInputToggleComponent,
  InfoBarComponent,
  InfoBarType,
  PreventLeaveConfirmationDialogComponent,
} from '@vdms-hq/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, firstValueFrom, startWith } from 'rxjs';
import { distinctUntilChanged, map, skip, switchMap } from 'rxjs/operators';
import { FieldValueByTypeToSelectOptionsPipe } from '../../field-value-by-type-to-select-options.pipe';
import { FieldValuesEditExtraComponent } from '../field-values-edit-extra/field-values-edit-extra.component';
import { FieldsDataSource } from '../../../field-definitions/logic/fields-data-source';

export type FieldValuesUpsertDialogInput = {
  fieldValue?: FieldValueViewModel;
  createOptions?: {
    type?: FieldTypeViewModel;
    label?: string;
  };
  selfServiceMode?: boolean;
  allowedFieldsTypeNames?: string[];
};

export type FieldValuesUpsertDialogResponse = {
  field: FieldValueViewModel | FieldValueExtendedViewModel;
};

/** Disallows double-quote and single-quote characters in a field value label. */
const noQuotesValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value: string = control.value ?? '';
  return /["']/.test(value) ? { noQuotes: true } : null;
};

@Component({
  selector: 'vdms-hq-selectors-field-values-upsert-dialog',
  templateUrl: './field-values-upsert-dialog.component.html',
  styleUrls: ['./field-values-upsert-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DialogWrapperComponent,
    FormsModule,
    ReactiveFormsModule,
    FormInputSelectComponent,
    ButtonComponent,
    TranslatePipe,
    FormInputToggleComponent,
    InfoBarComponent,
    AsyncPipe,
    FormInputTextAreaComponent,
    FieldValuesEditExtraComponent,
    FieldValueByTypeToSelectOptionsPipe,
    FormInputTextComponent,
    E2eIdDirective,
  ],
})
export class FieldValuesUpsertDialogComponent
  extends PreventLeaveConfirmationDialogComponent<FieldValuesUpsertDialogComponent, FieldValuesUpsertDialogResponse>
  implements OnInit, OnDestroy
{
  private apiFieldsSourceService = inject(ApiFieldsSourceService);
  private toastService = inject(ToastService);
  private superAdminAccessService = inject(SuperAdminAccessService);
  private destroyRef = inject(DestroyRef);
  private fieldsDataSource = inject(FieldsDataSource);
  data: FieldValuesUpsertDialogInput = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<FieldValuesUpsertDialogComponent, FieldValuesUpsertDialogResponse>);

  readonly isSuperAdmin = signal(false);
  readonly isSaving = signal(false);
  readonly InfoBarType = InfoBarType;
  readonly assetMasterTypes: SelectOption[] = AssetMasterType;

  readonly isEditMode = Boolean(this.data.fieldValue);
  readonly isSelfServiceMode = Boolean(this.data.selfServiceMode);

  readonly #initialSiblingsString = this.#siblingsToJson(this.data.fieldValue?.extra?.siblings);

  readonly #ownTypeName = this.data.fieldValue?.type?.name ?? null;
  readonly #parentField = this.data.fieldValue?.parent_field ?? null;

  /** Such a parent is a cycle, is unreachable through the dropdowns, and so renders as detached. */
  readonly detachedCyclicParent =
    !!this.#parentField &&
    (this.#parentField.uuid === this.data.fieldValue?.uuid || this.#parentField.type?.name === this.#ownTypeName);

  form = new FormGroup({
    type_uuid: new FormControl<string | null>(
      this.data.fieldValue?.type?.uuid ?? this.data?.createOptions?.type?.uuid ?? null,
      this.isEditMode ? [] : [Validators.required],
    ),
    label: new FormControl<string>(this.data.fieldValue?.label ?? this.data.createOptions?.label ?? '', [
      Validators.required,
    ]),
    description: new FormControl<string | null>(this.data.fieldValue?.description ?? null, this.isEditMode ? [] : []),
    is_global: new FormControl<boolean>(
      {
        value: Boolean(this.data.fieldValue?.is_global),
        disabled: true,
      },
      Validators.required,
    ),
    parent_field_type_name: new FormControl<string | null>(
      this.detachedCyclicParent ? null : (this.#parentField?.type?.name ?? null),
    ),
    parent_field_uuid: new FormControl<string | null>(
      this.detachedCyclicParent ? null : (this.#parentField?.uuid ?? null),
    ),
    extra: new FormGroup({
      siblings: new FormControl<string | undefined>(this.#initialSiblingsString),
      restricted: new FormControl<string[] | undefined>(
        Array.isArray(this.data.fieldValue?.extra?.restricted) ? this.data.fieldValue?.extra?.restricted : undefined,
      ),
    }),
  });

  types$ = combineLatest([this.apiFieldsSourceService.types$, this.fieldsDataSource.allData$]).pipe(
    map(([types, definitions]) => {
      let filtered = types;

      if (this.isSelfServiceMode) {
        const selfServiceTypeNames = new Set(
          definitions.filter((d) => d.isSelfService === true && d.sourceListKey).map((d) => d.sourceListKey as string),
        );
        filtered = filtered.filter((type) => selfServiceTypeNames.has(type.name));
      }

      let options: SelectOption[] = filtered.map(({ uuid, name }) => ({ key: uuid, label: name }));

      if (this.data.allowedFieldsTypeNames) {
        options = options.filter((option) => this.data.allowedFieldsTypeNames?.includes(option.label));
      }

      return options;
    }),
  );

  parentTypeNames$ = this.apiFieldsSourceService.typeNamesSelectOptions$.pipe(
    map((options) => options.filter((option) => option.key !== this.#ownTypeName)),
  );
  allFields$ = this.apiFieldsSourceService.fields$;
  newlyCreated$ = new BehaviorSubject<SelectOption | null>(null);

  /** Existing labels of the selected type, excluding this field's own and anything just added. */
  readonly #existingLabels = signal<string[]>([]);

  /** Blocks a label the selected type already has, on both the picker and the text input (#2048). */
  readonly #duplicateLabelValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '')
      .trim()
      .toLowerCase();

    return value && this.#existingLabels().includes(value) ? { duplicateLabel: true } : null;
  };

  autocompleteOptions$ = combineLatest([
    this.newlyCreated$,
    this.apiFieldsSourceService.types$,
    this.form.controls.type_uuid.valueChanges.pipe(startWith(null)),
  ]).pipe(
    map(([newlyCreated, types]) => {
      const typeUuid = this.form.controls.type_uuid.value;
      const fields =
        types
          .find((type) => type.uuid === typeUuid)
          ?.fields.map(
            (field) =>
              <SelectOption>{
                key: field.label,
                label: field.label,
                disabled: this.isSelfServiceMode,
              },
          ) ?? [];

      return newlyCreated ? [...fields, newlyCreated] : [...fields];
    }),
  );

  async ngOnInit() {
    if (this.isEditMode) {
      this.form.controls.type_uuid.disable();
      this.form.controls.is_global.disable();
    }

    if (!this.isEditMode && this.data.createOptions?.type) {
      this.form.controls.type_uuid.disable();
    }

    if (this.isSelfServiceMode && this.isEditMode) {
      this.form.controls.label.disable();
    }

    // A uuid held over from the previous type would silently re-attach that parent on save. The current
    // value seeds distinctUntilChanged so that re-picking the same type is not treated as a change.
    this.form.controls.parent_field_type_name.valueChanges
      .pipe(
        startWith(this.form.controls.parent_field_type_name.value),
        distinctUntilChanged(),
        skip(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.form.controls.parent_field_uuid.setValue(null));

    this.isSuperAdmin.set(await firstValueFrom(this.superAdminAccessService.isSuperAdmin$));

    if (!this.isEditMode && this.isSuperAdmin() && !this.isSelfServiceMode) {
      this.form.controls.is_global.enable();
    }

    // Conditionally apply noQuotesValidator only for the facility_of_origin type.
    this.form.controls.type_uuid.valueChanges
      .pipe(
        startWith(this.form.controls.type_uuid.getRawValue()),
        switchMap((uuid) =>
          this.apiFieldsSourceService.types$.pipe(map((types) => types.find((t) => t.uuid === uuid)?.name)),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((typeName) => {
        const labelControl = this.form.controls.label;
        if (typeName === OptionType.FACILITY_OF_ORIGIN) {
          labelControl.addValidators(noQuotesValidator);
        } else {
          labelControl.removeValidators(noQuotesValidator);
        }
        labelControl.updateValueAndValidity({ emitEvent: false });
      });

    // getRawValue(), because a disabled type control never emits valueChanges.
    this.form.controls.label.addValidators(this.#duplicateLabelValidator);
    combineLatest([
      this.apiFieldsSourceService.types$,
      this.form.controls.type_uuid.valueChanges.pipe(startWith(this.form.controls.type_uuid.getRawValue())),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([types, typeUuid]) => {
        const ownLabel = this.data.fieldValue?.label?.trim().toLowerCase();

        this.#existingLabels.set(
          (types.find((type) => type.uuid === typeUuid)?.fields ?? [])
            .map((field) => field.label?.trim().toLowerCase())
            .filter((label): label is string => !!label && label !== ownLabel),
        );
        this.form.controls.label.updateValueAndValidity({ emitEvent: false });
      });
  }

  ngOnDestroy() {
    this.destroy();
    super.onUnloadGuard();
  }

  save(): void {
    if (this.form.invalid || this.isSaving()) {
      return;
    }

    this.isSaving.set(true);

    const { type_uuid, label, is_global, description, parent_field_uuid, extra } = this.form.getRawValue();
    const trimmedLabel = label?.trim() ?? '';
    const sharedPayload: FieldValuePatchDto = {
      key: trimmedLabel,
      label: trimmedLabel,
      description: description ?? '',
      extra: extra ?? null,
      enabled: {
        in_forms: true,
        in_search: true,
      },
      parent_field_uuid: parent_field_uuid ?? null,
    };

    if (this.isEditMode) {
      const fieldUuid = this.data.fieldValue?.uuid;
      if (!fieldUuid) {
        this.isSaving.set(false);
        return;
      }

      this.apiFieldsSourceService.patchFieldValue(fieldUuid, sharedPayload).subscribe({
        next: (field) => {
          this.toastService.success({
            id: 'update_field_success',
            message: 'notifications.fields.update.success',
          });
          this.dialogRef.close({ field });
        },
        error: () => {
          this.toastService.error({
            id: 'update_field_failure',
            message: 'notifications.fields.update.failure',
          });
          this.isSaving.set(false);
        },
      });
      return;
    }

    this.apiFieldsSourceService
      .postFieldValue(<FieldValuePostDto>{
        ...sharedPayload,
        is_global: Boolean(is_global),
        type_uuid: type_uuid,
      })
      .subscribe({
        next: (field) => {
          this.toastService.success({
            id: 'create_field_success',
            message: 'notifications.fields.create.success',
          });

          this.dialogRef.close({ field });
        },
        error: (error: Error) => {
          this.toastService.error({
            id: 'create_field_failure',
            message: 'notifications.fields.create.failure',
            interpolatedParams: { reason: error?.message ?? 'Not specified' },
          });
          this.isSaving.set(false);
        },
      });
  }

  /** Detaching a cyclic parent leaves the form pristine, so that alone has to keep save reachable. */
  get isSaveDisabled(): boolean {
    return (
      this.form.invalid || this.isSaving() || (this.isEditMode && this.form.pristine && !this.detachedCyclicParent)
    );
  }

  cancel(): void {
    this.dialogRef.close();
  }

  addLabel(value: string) {
    this.newlyCreated$.next({ key: value, label: value });
    this.form.controls.label.patchValue(value);
  }

  override canClose() {
    return this.form.pristine;
  }

  #siblingsToJson(siblings: FieldValueSiblingViewModel[] | null | undefined): string | undefined {
    if (!siblings || siblings.length === 0) {
      return undefined;
    }

    return JSON.stringify({ siblings });
  }
}
